import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkAuth } from "../utils/api";
import { getImageUrl } from "../utils/imageUtils";

// Image Slideshow Component - Optimized with lazy loading
const ImageSlideshow = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set([0])); // Preload first image
  const loadedImagesRef = React.useRef(new Set([0])); // Track loaded images with ref to avoid re-renders
  
  const images = React.useMemo(() => [
    {
      src: "https://res.cloudinary.com/dmmetrvda/image/upload/v1766215619/weheal/banners/banner-1_healthcare.png",
      alt: "Doctor consultation",
      fallback: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=500"
    },
    {
      src: "https://res.cloudinary.com/dmmetrvda/image/upload/v1766215622/weheal/banners/banner-2_telemedicine.png",
      alt: "Healthcare services",
      fallback: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=500"
    }
  ], []);

  // Preload next image for smooth transition
  useEffect(() => {
    const nextIndex = (currentIndex + 1) % images.length;
    if (!loadedImagesRef.current.has(nextIndex)) {
      const img = new Image();
      img.src = images[nextIndex].src;
      img.onload = () => {
        loadedImagesRef.current.add(nextIndex);
        setLoadedImages(new Set(loadedImagesRef.current));
      };
      img.onerror = () => {
        // Silently handle preload errors - fallback will be used when image is rendered
      };
    }
  }, [currentIndex, images.length]);

  // Auto-rotate slideshow every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    // Load image when user clicks
    if (!loadedImagesRef.current.has(index)) {
      const img = new Image();
      img.src = images[index].src;
      img.onload = () => {
        loadedImagesRef.current.add(index);
        setLoadedImages(new Set(loadedImagesRef.current));
      };
    }
  };

  return (
    <div className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[500px] overflow-hidden rounded-2xl sm:rounded-3xl landing-slideshow">
      {/* Slideshow Container */}
      <div 
        className="flex transition-transform duration-500 ease-in-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((image, index) => {
          // Only render visible image and preloaded next image
          const isVisible = index === currentIndex;
          const isPreloaded = loadedImagesRef.current.has(index);
          
          // Don't render if not visible and not preloaded
          if (!isVisible && !isPreloaded) {
            return (
              <div
                key={index}
                className="min-w-full h-full flex-shrink-0"
                aria-hidden="true"
              />
            );
          }

          return (
            <div
              key={index}
              className="min-w-full h-full flex-shrink-0"
            >
              <img
                src={isPreloaded ? image.src : undefined}
                alt={image.alt}
                loading={index === 0 ? "eager" : "lazy"}
                className="w-full h-full object-cover"
                style={{ 
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%',
                  display: 'block'
                }}
                onError={(e) => {
                  e.target.src = image.fallback;
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Navigation Dots */}
      <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              currentIndex === index
                ? 'bg-white w-6'
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('landing-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Verify authentication and session validity
  useEffect(() => {
    const verifySession = async () => {
    try {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:verifySession-start',message:'Verifying session',data:{hasToken:!!localStorage.getItem('token'),hasUser:!!localStorage.getItem('user'),tokenValue:localStorage.getItem('token')?.substring(0,20),userValue:localStorage.getItem('user')?.substring(0,50)},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD1'})}).catch(()=>{});
        // #endregion
        
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
        
        if (!token || !storedUser) {
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:verifySession-no-token',message:'No token or user found',data:{hasToken:!!token,hasUser:!!storedUser},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD2'})}).catch(()=>{});
          // #endregion
          
          setIsAuthenticated(false);
          setCurrentUser(null);
          return;
        }

        // Verify session is still valid with backend
        try {
          const authResponse = await checkAuth();
          
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:verifySession-checkAuth-response',message:'checkAuth response received',data:{success:authResponse?.success,hasUser:!!authResponse?.user,userRole:authResponse?.user?.role,userEmail:authResponse?.user?.email},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD3'})}).catch(()=>{});
          // #endregion
          
          if (authResponse.success && authResponse.user) {
        setIsAuthenticated(true);
            setCurrentUser(authResponse.user);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(authResponse.user));
            
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:verifySession-authenticated',message:'User authenticated',data:{userRole:authResponse.user.role,userEmail:authResponse.user.email},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD4'})}).catch(()=>{});
            // #endregion
      } else {
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:verifySession-auth-failed',message:'Authentication failed - clearing state',data:{success:authResponse?.success,hasUser:!!authResponse?.user},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD5'})}).catch(()=>{});
            // #endregion
            
        setIsAuthenticated(false);
        setCurrentUser(null);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          // Session expired or invalid, clear auth state
          console.log('Session verification failed:', error);
          
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:verifySession-error',message:'Session verification error',data:{errorMessage:error?.message,errorStatus:error?.response?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD6'})}).catch(()=>{});
          // #endregion
          
      setIsAuthenticated(false);
      setCurrentUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error verifying session:', error);
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    };

    verifySession();
    
    // Listen for storage changes (e.g., logout from another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'user') {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:handleStorageChange',message:'Storage change detected',data:{key:e.key,newValue:e.newValue?.substring(0,50)},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD7'})}).catch(()=>{});
        // #endregion
        
        verifySession();
      }
    };
    
    // Listen for custom logout event (same-tab logout)
    const handleLogout = () => {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:handleLogout',message:'Logout event received',data:{hasToken:!!localStorage.getItem('token'),hasUser:!!localStorage.getItem('user')},timestamp:Date.now(),sessionId:'debug-session',runId:'landing-debug',hypothesisId:'LD8'})}).catch(()=>{});
      // #endregion
      
      setIsAuthenticated(false);
      setCurrentUser(null);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogout', handleLogout);
    
    // Also verify when page becomes visible (user navigates back)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        verifySession();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogout', handleLogout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Noto+Sans+Bengali:wght@400;600&display=swap';
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) document.head.removeChild(link);
    };
  }, []);

  // Dark mode toggle
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('landing-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  // Morph animation handler for navigation buttons
  const handleMorphClick = (e, callback) => {
    const button = e.currentTarget;
    
    // Add morph-active class
    button.classList.add('morph-active');
    
    // Remove class after animation completes
    setTimeout(() => {
      button.classList.remove('morph-active');
    }, 600);
    
    // Execute callback if provided
    if (callback) {
      setTimeout(() => {
        callback();
      }, 300);
    }
  };

  const dashboardPath = useMemo(() => {
    const role = currentUser?.role;
    switch (role) {
      case 'Admin':
        return '/dashboard/admin';
      case 'Doctor':
        return '/dashboard/doctor';
      case 'Driver':
        return '/dashboard/driver';
      case 'Nurse':
        return '/dashboard/nurse';
      case 'ClinicStaff':
        return '/dashboard/clinic-staff';
      case 'CustomerCare':
        return '/dashboard/customer-care';
      default:
        return '/dashboard/patient';
    }
  }, [currentUser]);

  const handleNavAuthRedirect = (pathIfAuthed = dashboardPath) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(pathIfAuthed);
    setIsNavOpen(false);
  };

  const handleHomeClick = () => {
    if (isAuthenticated) {
      navigate(dashboardPath);
    } else {
      // Scroll to top of landing page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsNavOpen(false);
  };

  const handleSignUp = () => {
    navigate("/signup");
  };

  const handleSignIn = () => {
    // If authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate(dashboardPath);
    } else {
    navigate("/login");
    }
  };

  const handleAppointment = () => {
    if (isAuthenticated) {
      // Redirect to patient dashboard (where Find Doctor is available)
      // For patients, go to dashboard. For other roles, go to their dashboard
      if (currentUser?.role === 'Patient' || !currentUser?.role) {
        navigate('/dashboard/patient');
      } else {
        navigate(dashboardPath);
      }
    } else {
      navigate("/login");
    }
  };

  const handleEmergency = () => {
    if (isAuthenticated) {
      navigate("/emergency");
    } else {
      navigate("/login");
    }
  };

  // Counter animation for stats section
  useEffect(() => {
    const counters = document.querySelectorAll('.counter-value');
    if (counters.length === 0) return;

    let hasAnimated = false;
    const durationMs = 2000;

    const animate = (el) => {
      const target = Number(el.getAttribute('data-target'));
      const start = 0;
      const startTime = performance.now();

      const step = (now) => {
        const progress = Math.min((now - startTime) / durationMs, 1);
        const value = Math.floor(start + (target - start) * progress);
        el.textContent = value;
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    };

    const onIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;
          counters.forEach((el) => animate(el));
          observer.disconnect();
        }
      });
    };

    const observer = new IntersectionObserver(onIntersect, { threshold: 0.3 });
    const section = document.querySelector('.stats-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  // Scroll-triggered animations for elements
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:161',message:'Scroll animation setup started',data:{timestamp:Date.now()},sessionId:'debug-session'})}).catch(()=>{});
    // #endregion
    
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll, .animate-on-scroll-delay');
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:166',message:'Checking scroll elements',data:{elementCount:elements.length,timestamp:Date.now()},sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      elements.forEach((el) => {
        const elementTop = el.getBoundingClientRect().top;
        const elementVisible = 150;
        
        // Mark as visible if already in viewport or above it, or if element is anywhere on screen
        if (elementTop < window.innerHeight + 500) { // More lenient check - mark visible if anywhere near viewport
          el.classList.add('visible');
          // Force visibility with inline style as backup
          el.style.opacity = '1';
          el.style.visibility = 'visible';
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:175',message:'Element made visible',data:{className:el.className,hasVisible:el.classList.contains('visible'),elementTop:elementTop,windowHeight:window.innerHeight,inlineOpacity:el.style.opacity,inlineVisibility:el.style.visibility,timestamp:Date.now()},sessionId:'debug-session'})}).catch(()=>{});
          // #endregion
        }
      });
    };

    window.addEventListener('scroll', animateOnScroll);
    animateOnScroll(); // Run once on mount

    // #region agent log
    setTimeout(() => {
      const container = document.querySelector('.landing-page-container');
      const sections = document.querySelectorAll('section');
      const howItWorksSection = document.querySelector('#how-it-works');
      const servicesSection = document.querySelector('#services');
      const heroSection = document.querySelector('section:first-of-type');
      const heroImage = document.querySelector('img[alt="Doctor consultation"]');
      const howItWorksCards = document.querySelectorAll('#how-it-works .how-it-works-card');
      const serviceCards = document.querySelectorAll('#services .service-card');
      const benefitCards = document.querySelectorAll('.benefit-card');
      
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Landing.js:189',message:'Comprehensive visibility check',data:{
        containerExists:!!container,
        containerOpacity:container ? window.getComputedStyle(container).opacity : null,
        sectionsCount:sections.length,
        heroSectionExists:!!heroSection,
        heroSectionOpacity:heroSection ? window.getComputedStyle(heroSection).opacity : null,
        heroImageExists:!!heroImage,
        heroImageDisplay:heroImage ? window.getComputedStyle(heroImage).display : null,
        howItWorksExists:!!howItWorksSection,
        howItWorksVisible:howItWorksSection ? window.getComputedStyle(howItWorksSection).opacity : null,
        servicesExists:!!servicesSection,
        servicesVisible:servicesSection ? window.getComputedStyle(servicesSection).opacity : null,
        howItWorksCardsCount:howItWorksCards.length,
        serviceCardsCount:serviceCards.length,
        benefitCardsCount:benefitCards.length,
        howItWorksCardsVisible:Array.from(howItWorksCards).slice(0,2).map(c => ({
          opacity:window.getComputedStyle(c).opacity,
          hasVisible:c.classList.contains('visible')
        })),
        serviceCardsVisible:Array.from(serviceCards).slice(0,2).map(c => ({
          opacity:window.getComputedStyle(c).opacity,
          hasVisible:c.classList.contains('visible')
        })),
        benefitCardsVisible:Array.from(benefitCards).slice(0,2).map(c => ({
          opacity:window.getComputedStyle(c).opacity,
          hasVisible:c.classList.contains('visible')
        })),
        timestamp:Date.now()
      },sessionId:'debug-session'})}).catch(()=>{});
    }, 2000);
    // #endregion

    return () => window.removeEventListener('scroll', animateOnScroll);
  }, []);

  // Cursor-reactive animated background - Full Page
  useEffect(() => {
    const cursorBg = document.querySelector('.cursor-reactive-bg');
    if (!cursorBg) return;

    // Keep background fixed to viewport but update on scroll for full coverage
    const updateHeight = () => {
      const pageHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      cursorBg.style.height = pageHeight + 'px';
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    
    // Update on scroll to ensure coverage
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateHeight, 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const glow = document.createElement('div');
    glow.className = 'cursor-reactive-glow';
    cursorBg.appendChild(glow);

    const particles = [];
    const particleCount = 80; // Increased for full page coverage

    // Create particles distributed across full page
    const createParticles = () => {
      const pageHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'cursor-reactive-particle';
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * pageHeight;
      particle.style.left = x + 'px';
      particle.style.top = y + 'px';
      particle.style.opacity = Math.random() * 0.3 + 0.7;
        cursorBg.appendChild(particle);
        particles.push({ 
          element: particle, 
          x, 
          y, 
          vx: (Math.random() - 0.5) * 0.5, 
          vy: (Math.random() - 0.5) * 0.5 
        });
      }
    };
    createParticles();

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY + window.scrollY; // Account for scroll
      glow.style.left = mouseX + 'px';
      glow.style.top = mouseY + 'px';
    };

    const animate = () => {
      const pageHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      
      particles.forEach((particle) => {
        // Move particles slightly
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > window.innerWidth) particle.vx *= -1;
        if (particle.y < 0 || particle.y > pageHeight) particle.vy *= -1;

        // Attract to cursor
        const dx = mouseX - particle.x;
        const dy = mouseY - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 200;
        
        if (distance < maxDistance) {
          const force = (maxDistance - distance) / maxDistance;
          particle.x += dx * force * 0.02;
          particle.y += dy * force * 0.02;
          particle.element.style.opacity = Math.min(0.9, 0.5 + force * 0.4);
          particle.element.style.transform = `scale(${1 + force * 0.5})`;
        } else {
          particle.element.style.opacity = 0.5 + Math.random() * 0.3;
          particle.element.style.transform = 'scale(1)';
        }

        particle.element.style.left = particle.x + 'px';
        particle.element.style.top = particle.y + 'px';
      });

      requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', () => {
      mouseY = mouseY + window.scrollY;
    });
    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', handleScroll);
      cursorBg.innerHTML = '';
    };
  }, []);

  // Appointment button highlighting animation - Create flowing particles
  useEffect(() => {
    const appointmentButton = document.querySelector('.appointment-button-highlight');
    if (!appointmentButton) return;

    const particlesContainer = appointmentButton.querySelector('.appointment-particles');
    if (!particlesContainer) return;

    // Create multiple particles that flow towards the button
    const createParticle = (delay, startX, startY, endX, endY) => {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 8px;
        height: 8px;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(59, 130, 246, 0.6));
        border-radius: 50%;
        pointer-events: none;
        left: ${startX}px;
        top: ${startY}px;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.8);
        animation: flowToButton 2.5s ease-in-out infinite;
        animation-delay: ${delay}s;
        z-index: 1;
      `;
      
      // Add keyframes dynamically
      if (!document.getElementById('particle-flow-animation')) {
        const style = document.createElement('style');
        style.id = 'particle-flow-animation';
        style.textContent = `
          @keyframes flowToButton {
            0% {
              transform: translate(0, 0) scale(0);
              opacity: 0;
            }
            10% {
              opacity: 1;
            }
            90% {
              opacity: 1;
            }
            100% {
              transform: translate(${endX - startX}px, ${endY - startY}px) scale(1.5);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }
      
      particlesContainer.appendChild(particle);
    };

    // Create particles from different directions
    const buttonRect = appointmentButton.getBoundingClientRect();
    const centerX = buttonRect.width / 2;
    const centerY = buttonRect.height / 2;

    // Particles from top-left
    for (let i = 0; i < 3; i++) {
      createParticle(i * 0.3, -50 - i * 20, -30 - i * 15, centerX, centerY);
    }

    // Particles from top-right
    for (let i = 0; i < 3; i++) {
      createParticle(i * 0.3 + 0.5, buttonRect.width + 50 + i * 20, -30 - i * 15, centerX, centerY);
    }

    // Particles from bottom
    for (let i = 0; i < 2; i++) {
      createParticle(i * 0.4 + 1, centerX + (i % 2 === 0 ? -30 : 30), buttonRect.height + 40, centerX, centerY);
    }

    return () => {
      const styleEl = document.getElementById('particle-flow-animation');
      if (styleEl) styleEl.remove();
    };
  }, []);

  // Emergency button highlighting animation - Create flowing particles
  useEffect(() => {
    // Add keyframes for emergency particles if not exists
    if (!document.getElementById('emergency-particle-flow-animation')) {
      const style = document.createElement('style');
      style.id = 'emergency-particle-flow-animation';
      style.textContent = `
        @keyframes flowToEmergencyButton {
          0% {
            transform: translate(0, 0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--target-x, 0), var(--target-y, 0)) scale(1.5);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const emergencyButtons = document.querySelectorAll('.emergency-button-highlight');
    if (emergencyButtons.length === 0) return;

    emergencyButtons.forEach((emergencyButton) => {
      const particlesContainer = emergencyButton.querySelector('.emergency-particles');
      if (!particlesContainer) return;

      // Create multiple particles that flow towards the button
      const createEmergencyParticle = (delay, startX, startY, endX, endY) => {
        const particle = document.createElement('div');
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        
        particle.style.cssText = `
          position: absolute;
          width: 8px;
          height: 8px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(239, 68, 68, 0.6));
          border-radius: 50%;
          pointer-events: none;
          left: ${startX}px;
          top: ${startY}px;
          box-shadow: 0 0 10px rgba(239, 68, 68, 0.8);
          --target-x: ${deltaX}px;
          --target-y: ${deltaY}px;
          animation: flowToEmergencyButton 2.5s ease-in-out infinite;
          animation-delay: ${delay}s;
          z-index: 1;
        `;
        
        particlesContainer.appendChild(particle);
      };

      // Create particles from different directions
      const buttonRect = emergencyButton.getBoundingClientRect();
      const centerX = buttonRect.width / 2;
      const centerY = buttonRect.height / 2;

      // Particles from top-left
      for (let i = 0; i < 3; i++) {
        createEmergencyParticle(i * 0.3, -50 - i * 20, -30 - i * 15, centerX, centerY);
      }

      // Particles from top-right
      for (let i = 0; i < 3; i++) {
        createEmergencyParticle(i * 0.3 + 0.5, buttonRect.width + 50 + i * 20, -30 - i * 15, centerX, centerY);
      }

      // Particles from bottom
      for (let i = 0; i < 2; i++) {
        createEmergencyParticle(i * 0.4 + 1, centerX + (i % 2 === 0 ? -30 : 30), buttonRect.height + 40, centerX, centerY);
      }
    });

    return () => {
      const styleEl = document.getElementById('emergency-particle-flow-animation');
      if (styleEl) styleEl.remove();
    };
  }, []);

  return (
    <div className="landing-page-container bg-white dark:bg-gray-900 relative min-h-screen transition-colors duration-300" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-4 right-4 sm:top-4 sm:right-4 z-[100] p-2.5 sm:p-3 rounded-full bg-white dark:bg-gray-800 backdrop-blur-md shadow-xl border-2 border-gray-300 dark:border-gray-600 hover:shadow-2xl transition-all duration-200 dark-mode-toggle-btn"
        style={{ 
          minWidth: '44px',
          minHeight: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Cursor Reactive Background - Full Page */}
      <div className="cursor-reactive-bg"></div>
      
      {/* Global Styles */}
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { margin: 0; padding: 0; overflow-x: hidden; }
        #root { padding: 0 !important; margin: 0 !important; max-width: none !important; }
        
        :root {
          --primary-color: #1E40AF;
          --primary-light: #2563EB;
          --primary-dark: #1E3A8A;
          --secondary-color: #1F2937;
          --accent-color: #3B82F6;
          --light-bg: #F9FAFB;
          --text-primary: #111827;
          --text-secondary: #6B7280;
        }

        [data-theme="dark"] {
          --primary-color: #3B82F6;
          --primary-light: #60A5FA;
          --primary-dark: #2563EB;
          --secondary-color: #E5E7EB;
          --accent-color: #60A5FA;
          --light-bg: #1F2937;
          --text-primary: #F9FAFB;
          --text-secondary: #D1D5DB;
        }

        /* Cursor Reactive Background - Full Page */
        .cursor-reactive-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          pointer-events: none;
          z-index: 1;
          overflow: hidden;
        }

        /* Ensure background covers entire page */
        body {
          position: relative;
        }

        /* Sections with backgrounds that allow particles to show through */
        section {
          position: relative;
          z-index: 2;
        }

        /* Make section backgrounds slightly transparent to show particles */
        section.bg-white {
          background: rgba(255, 255, 255, 0.92) !important;
          backdrop-filter: blur(1px);
        }

        section.bg-gray-50 {
          background: rgba(249, 250, 251, 0.92) !important;
          backdrop-filter: blur(1px);
        }

        section.bg-gray-900 {
          background: rgba(17, 24, 39, 0.96) !important;
        }

        section.bg-gradient-to-br {
          background: linear-gradient(to bottom right, rgba(249, 250, 251, 0.92), rgba(255, 255, 255, 0.92)) !important;
          backdrop-filter: blur(1px);
        }

        /* Dark Mode Section Backgrounds */
        [data-theme="dark"] section.bg-white {
          background: rgba(17, 24, 39, 0.92) !important;
        }

        [data-theme="dark"] section.bg-gray-50 {
          background: rgba(31, 41, 55, 0.92) !important;
        }

        [data-theme="dark"] section.bg-gray-900 {
          background: rgba(3, 7, 18, 0.98) !important;
        }

        [data-theme="dark"] section.bg-gradient-to-br {
          background: linear-gradient(to bottom right, rgba(17, 24, 39, 0.92), rgba(31, 41, 55, 0.92)) !important;
        }

        /* Navigation stays on top */
        nav {
          position: relative;
          z-index: 50;
        }

        /* Content containers */
        div[class*="max-w"] {
          position: relative;
          z-index: 3;
        }

        .cursor-reactive-particle {
          position: absolute;
          width: 10px;
          height: 10px;
          background: radial-gradient(circle, rgba(30, 64, 175, 1), rgba(37, 99, 235, 0.8), rgba(59, 130, 246, 0.4));
          border-radius: 50%;
          pointer-events: none;
          transition: transform 0.3s ease-out, opacity 0.3s ease-out;
          box-shadow: 0 0 20px rgba(30, 64, 175, 1), 0 0 40px rgba(37, 99, 235, 0.6), 0 0 60px rgba(59, 130, 246, 0.3);
          z-index: 1;
        }

        .cursor-reactive-glow {
          position: absolute;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(30, 64, 175, 0.4), rgba(37, 99, 235, 0.25), rgba(59, 130, 246, 0.1), transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          transform: translate(-50%, -50%);
          transition: transform 0.1s ease-out;
          filter: blur(50px);
          z-index: 1;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes floatReverse {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-25px) rotate(-5deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes wave {
          0%, 100% { transform: translateX(0) translateY(0); }
          50% { transform: translateX(-25px) translateY(-10px); }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-on-scroll {
          opacity: 1 !important;
          transform: translateY(0) !important;
          transition: all 0.6s ease-out;
        }

        .animate-on-scroll.visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }

        .animate-on-scroll-delay {
          opacity: 1 !important;
          transform: translateX(0) !important;
          transition: all 0.8s ease-out;
        }

        .animate-on-scroll-delay.visible {
          opacity: 1 !important;
          transform: translateX(0) !important;
        }

        /* Force visibility for all cards */
        .how-it-works-card,
        .service-card,
        .benefit-card {
          opacity: 1 !important;
          visibility: visible !important;
        }


        /* Benefit Cards Animation - Advanced */
        .benefit-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .benefit-card::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(30, 64, 175, 0.1), transparent);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }

        .benefit-card:hover::after {
          width: 300px;
          height: 300px;
        }

        .benefit-card:hover {
          transform: translateY(-12px) scale(1.03);
          box-shadow: 0 20px 40px rgba(30, 64, 175, 0.2);
        }

        .benefit-icon {
          display: inline-block;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          filter: drop-shadow(0 4px 8px rgba(30, 64, 175, 0.3));
        }

        .benefit-card:hover .benefit-icon {
          transform: scale(1.3) rotate(10deg) translateY(-8px);
          filter: drop-shadow(0 12px 24px rgba(30, 64, 175, 0.5));
        }

        /* How It Works Cards - Advanced */
        .how-it-works-card {
          position: relative;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .how-it-works-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #1E40AF, #2563EB, #3B82F6);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .how-it-works-card:hover::before {
          transform: scaleX(1);
        }

        .how-it-works-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 16px 32px rgba(30, 64, 175, 0.15);
        }

        .step-number {
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.1), rgba(37, 99, 235, 0.05));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .how-it-works-card:hover .step-number {
          transform: scale(1.2) rotate(5deg);
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.3), rgba(37, 99, 235, 0.2));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .step-connector {
          position: absolute;
          top: 50%;
          right: -30px;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, rgba(30, 64, 175, 0.4), rgba(37, 99, 235, 0.2), transparent);
          display: none;
          border-radius: 2px;
          box-shadow: 0 0 10px rgba(30, 64, 175, 0.3);
          animation: pulse 2s ease-in-out infinite;
        }

        @media (min-width: 768px) {
          .step-connector {
            display: block;
          }
        }

        /* Stats Section Advanced Animations */
        .stat-card-animated {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          padding: 1.5rem;
          border-radius: 1rem;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .stat-card-animated:hover {
          transform: translateY(-10px) scale(1.05);
          background: rgba(255, 255, 255, 0.1);
          box-shadow: 0 20px 40px rgba(30, 64, 175, 0.3);
          border-color: rgba(59, 130, 246, 0.5);
        }

        /* Dark Mode Styles */
        [data-theme="dark"] {
          color: var(--text-primary);
        }

        [data-theme="dark"] .bg-white {
          background-color: #111827 !important;
        }

        [data-theme="dark"] .text-gray-900 {
          color: #F9FAFB !important;
        }

        [data-theme="dark"] .text-gray-600 {
          color: #D1D5DB !important;
        }

        [data-theme="dark"] .text-gray-700 {
          color: #E5E7EB !important;
        }

        [data-theme="dark"] .text-gray-500 {
          color: #9CA3AF !important;
        }

        [data-theme="dark"] .text-gray-400 {
          color: #9CA3AF !important;
        }

        [data-theme="dark"] .bg-gray-50 {
          background-color: #1F2937 !important;
        }

        [data-theme="dark"] .bg-gray-100 {
          background-color: #374151 !important;
        }

        [data-theme="dark"] .border-gray-100,
        [data-theme="dark"] .border-gray-200,
        [data-theme="dark"] .border-gray-300 {
          border-color: #374151 !important;
        }

        [data-theme="dark"] .service-card {
          background: linear-gradient(135deg, rgba(31, 41, 55, 0.9), rgba(17, 24, 39, 0.9)) !important;
          border-color: #374151 !important;
        }

        [data-theme="dark"] .service-card:hover {
          border-color: rgba(59, 130, 246, 0.5) !important;
          box-shadow: 0 20px 40px rgba(59, 130, 246, 0.2) !important;
        }

        [data-theme="dark"] .benefit-card {
          background-color: #1F2937 !important;
          border-color: #374151 !important;
        }

        [data-theme="dark"] .how-it-works-card {
          background-color: #1F2937 !important;
          border-color: #374151 !important;
        }

        [data-theme="dark"] .cursor-reactive-particle {
          box-shadow: 0 0 20px rgba(59, 130, 246, 1), 0 0 40px rgba(96, 165, 250, 0.8), 0 0 60px rgba(147, 197, 253, 0.5) !important;
        }

        [data-theme="dark"] .cursor-reactive-glow {
          background: radial-gradient(circle, rgba(59, 130, 246, 0.5), rgba(96, 165, 250, 0.3), rgba(147, 197, 253, 0.15), transparent 70%) !important;
        }

        /* Dark Mode - Top Contact Bar */
        [data-theme="dark"] .bg-white.border-b {
          background-color: #111827 !important;
          border-color: #374151 !important;
        }

        /* Dark Mode - Navigation */
        [data-theme="dark"] nav.bg-gray-900 {
          background-color: #030712 !important;
        }

        /* Dark Mode - Hero Section */
        [data-theme="dark"] .bg-gradient-to-br.from-gray-50.to-white {
          background: linear-gradient(to bottom right, #111827, #1F2937) !important;
        }

        /* Dark Mode - Cards and Containers */
        [data-theme="dark"] .rounded-xl,
        [data-theme="dark"] .rounded-2xl,
        [data-theme="dark"] .rounded-3xl {
          background-color: #1F2937 !important;
        }

        [data-theme="dark"] .bg-white.rounded-2xl {
          background-color: #1F2937 !important;
        }

        /* Dark Mode - Buttons */
        [data-theme="dark"] .bg-blue-600 {
          background-color: #2563EB !important;
        }

        [data-theme="dark"] .bg-blue-600:hover {
          background-color: #1D4ED8 !important;
        }

        [data-theme="dark"] .bg-white.text-blue-600 {
          background-color: #1F2937 !important;
          color: #60A5FA !important;
        }

        [data-theme="dark"] .bg-white.text-blue-600:hover {
          background-color: #374151 !important;
        }

        /* Dark Mode - Pricing Cards */
        [data-theme="dark"] .bg-gray-900.rounded-xl {
          background-color: #030712 !important;
        }

        /* Dark Mode - FAQ Section */
        [data-theme="dark"] .bg-gray-50.rounded-lg {
          background-color: #1F2937 !important;
        }

        /* Dark Mode - Text Colors */
        [data-theme="dark"] h1,
        [data-theme="dark"] h2,
        [data-theme="dark"] h3,
        [data-theme="dark"] h4 {
          color: #F9FAFB !important;
        }

        [data-theme="dark"] p {
          color: #D1D5DB !important;
        }

        /* Dark Mode - Links */
        [data-theme="dark"] a {
          color: #60A5FA !important;
        }

        [data-theme="dark"] a:hover {
          color: #93C5FD !important;
        }

        /* Dark Mode - Input Fields */
        [data-theme="dark"] input,
        [data-theme="dark"] textarea,
        [data-theme="dark"] select {
          background-color: #1F2937 !important;
          color: #F9FAFB !important;
          border-color: #374151 !important;
        }

        /* Dark Mode - Toggle Button */
        [data-theme="dark"] .dark-mode-toggle-btn {
          background: rgba(31, 41, 55, 0.9) !important;
          border-color: rgba(55, 65, 81, 0.6) !important;
        }

        [data-theme="dark"] .dark-mode-toggle-btn:hover {
          background: rgba(55, 65, 81, 0.9) !important;
        }

        .stat-icon {
          display: inline-block;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          filter: drop-shadow(0 4px 8px rgba(59, 130, 246, 0.4));
        }

        .stat-card-animated:hover .stat-icon {
          transform: scale(1.3) rotate(10deg) translateY(-5px);
          filter: drop-shadow(0 12px 24px rgba(59, 130, 246, 0.6));
        }

        /* Hero Section Advanced Animation */
        .hero-cta-button {
          position: relative;
          overflow: hidden;
        }

        .hero-cta-button::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }

        .hero-cta-button:hover::before {
          width: 300px;
          height: 300px;
        }

        .hero-cta-button span {
          position: relative;
          z-index: 1;
        }

        /* Appointment Button Highlighting Animation */
        @keyframes appointmentPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7), 0 0 0 0 rgba(37, 99, 235, 0.7);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 20px rgba(59, 130, 246, 0), 0 0 0 40px rgba(37, 99, 235, 0);
          }
        }

        @keyframes appointmentGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(37, 99, 235, 0.4), 0 0 60px rgba(30, 64, 175, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(59, 130, 246, 0.9), 0 0 60px rgba(37, 99, 235, 0.6), 0 0 90px rgba(30, 64, 175, 0.4);
          }
        }

        @keyframes particleFlow {
          0% {
            transform: translate(0, 0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--target-x, 0), var(--target-y, 0)) scale(1);
            opacity: 0;
          }
        }

        @keyframes spotlight {
          0%, 100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1.2);
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-50%) rotate(45deg);
          }
          100% {
            transform: translateX(200%) translateY(-50%) rotate(45deg);
          }
        }

        .appointment-button-highlight {
          animation: appointmentPulse 2s ease-in-out infinite, appointmentGlow 3s ease-in-out infinite;
          position: relative;
        }

        .appointment-particles {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .appointment-particles::before,
        .appointment-particles::after {
          content: '';
          position: absolute;
          width: 6px;
          height: 6px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.9), transparent);
          border-radius: 50%;
          animation: particleFlow 3s ease-in-out infinite;
        }

        .appointment-particles::before {
          top: -30px;
          left: 20%;
          --target-x: 30px;
          --target-y: 50px;
          animation-delay: 0s;
        }

        .appointment-particles::after {
          top: -30px;
          right: 20%;
          --target-x: -30px;
          --target-y: 50px;
          animation-delay: 1.5s;
        }

        .appointment-spotlight {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent 70%);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          z-index: 0;
          animation: spotlight 2s ease-in-out infinite;
          filter: blur(20px);
        }

        .appointment-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transform: skewX(-20deg);
          pointer-events: none;
          z-index: 2;
          animation: shine 3s ease-in-out infinite;
        }

        /* Dark mode adjustments for appointment button */
        [data-theme="dark"] .appointment-button-highlight {
          animation: appointmentPulse 2s ease-in-out infinite, appointmentGlow 3s ease-in-out infinite;
        }

        [data-theme="dark"] .appointment-spotlight {
          background: radial-gradient(circle, rgba(59, 130, 246, 0.4), transparent 70%);
        }

        /* Emergency Button Highlighting Animation - Red Theme */
        @keyframes emergencyPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7), 0 0 0 0 rgba(220, 38, 38, 0.7);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 20px rgba(239, 68, 68, 0), 0 0 0 40px rgba(220, 38, 38, 0);
          }
        }

        @keyframes emergencyGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.6), 0 0 40px rgba(220, 38, 38, 0.4), 0 0 60px rgba(185, 28, 28, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(239, 68, 68, 0.9), 0 0 60px rgba(220, 38, 38, 0.6), 0 0 90px rgba(185, 28, 28, 0.4);
          }
        }

        @keyframes emergencyParticleFlow {
          0% {
            transform: translate(0, 0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--target-x, 0), var(--target-y, 0)) scale(1);
            opacity: 0;
          }
        }

        @keyframes emergencySpotlight {
          0%, 100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1.2);
          }
        }

        @keyframes emergencyShine {
          0% {
            transform: translateX(-100%) translateY(-50%) rotate(45deg);
          }
          100% {
            transform: translateX(200%) translateY(-50%) rotate(45deg);
          }
        }

        .emergency-button-highlight {
          animation: emergencyPulse 1.5s ease-in-out infinite, emergencyGlow 2.5s ease-in-out infinite;
          position: relative;
        }

        .emergency-particles {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .emergency-particles::before,
        .emergency-particles::after {
          content: '';
          position: absolute;
          width: 6px;
          height: 6px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(239, 68, 68, 0.6));
          border-radius: 50%;
          animation: emergencyParticleFlow 2.5s ease-in-out infinite;
        }

        .emergency-particles::before {
          top: -30px;
          left: 20%;
          --target-x: 30px;
          --target-y: 50px;
          animation-delay: 0s;
        }

        .emergency-particles::after {
          top: -30px;
          right: 20%;
          --target-x: -30px;
          --target-y: 50px;
          animation-delay: 1.25s;
        }

        .emergency-spotlight {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.15), transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          transform: translate(-50%, -50%);
          transition: transform 0.1s ease-out;
          filter: blur(40px);
          z-index: 1;
          animation: emergencySpotlight 1.5s ease-in-out infinite;
        }

        .emergency-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transform: skewX(-20deg);
          pointer-events: none;
          z-index: 2;
          animation: emergencyShine 2.5s ease-in-out infinite;
        }

        /* Dark mode adjustments for emergency button */
        [data-theme="dark"] .emergency-button-highlight {
          animation: emergencyPulse 1.5s ease-in-out infinite, emergencyGlow 2.5s ease-in-out infinite;
        }

        [data-theme="dark"] .emergency-spotlight {
          background: radial-gradient(circle, rgba(239, 68, 68, 0.4), rgba(220, 38, 38, 0.25), transparent 70%);
        }

        /* Service Cards Animation - Advanced */
        .service-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.9));
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(30, 64, 175, 0.1), transparent);
          transition: left 0.6s ease;
        }

        .service-card:hover::before {
          left: 100%;
        }

        .service-card:hover {
          transform: translateY(-12px) scale(1.03);
          box-shadow: 0 20px 40px rgba(30, 64, 175, 0.15);
          border-color: rgba(30, 64, 175, 0.3);
        }

        .service-icon {
          display: inline-block;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          filter: drop-shadow(0 2px 4px rgba(30, 64, 175, 0.2));
        }

        .service-card:hover .service-icon {
          transform: scale(1.4) rotate(15deg) translateY(-5px);
          filter: drop-shadow(0 8px 16px rgba(30, 64, 175, 0.4));
        }

        .service-button {
          position: relative;
          transition: all 0.3s ease;
        }

        .service-card:hover .service-button {
          transform: translateX(8px);
          color: #1E40AF;
          font-weight: 600;
        }

        /* Animated Gradient Background for Service Cards */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .service-card {
          background-size: 200% 200%;
          animation: gradientShift 8s ease infinite;
        }

        .wave-1 {
          animation-delay: 0s;
        }

        /* Prevent any overlapping */
        section {
          position: relative;
          width: 100%;
          clear: both;
        }

        img {
          max-width: 100%;
          height: auto;
          display: block;
        }

        /* Elegant Fade-In with Slide Animation */
        @keyframes elegantFadeIn {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInFromLeft {
          0% {
            opacity: 0;
            transform: translateX(-40px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInFromRight {
          0% {
            opacity: 0;
            transform: translateX(40px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleFadeIn {
          0% {
            opacity: 1;
            transform: scale(0.98);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* Main container - smooth fade in */
        .landing-page-container {
          animation: elegantFadeIn 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        /* Navigation - slide from top */
        .landing-page-container nav {
          animation: slideInFromLeft 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          animation-delay: 0.1s;
          opacity: 0;
        }

        /* Top contact bar - slide from top */
        .top-contact-bar {
          animation: slideInFromLeft 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          animation-delay: 0.15s;
          opacity: 0;
        }

        /* Sections - staggered fade in with slight scale */
        /* Start visible but with slight scale, then animate to full scale */
        .landing-page-container section {
          opacity: 1 !important; /* Force visible from start */
          transform: scale(1) !important;
          visibility: visible !important;
          animation: scaleFadeIn 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        /* Ensure cards and key content elements are visible */
        .landing-page-container section .how-it-works-card,
        .landing-page-container section .service-card,
        .landing-page-container section .benefit-card,
        .landing-page-container section h2,
        .landing-page-container section h3,
        .landing-page-container section p,
        .landing-page-container section img {
          opacity: 1 !important;
          visibility: visible !important;
        }

        .landing-page-container section:nth-of-type(1) {
          animation-delay: 0.2s;
        }

        .landing-page-container section:nth-of-type(2) {
          animation-delay: 0.3s;
        }

        .landing-page-container section:nth-of-type(3) {
          animation-delay: 0.4s;
        }

        .landing-page-container section:nth-of-type(4) {
          animation-delay: 0.5s;
        }

        .landing-page-container section:nth-of-type(5) {
          animation-delay: 0.6s;
        }

        .landing-page-container section:nth-of-type(6) {
          animation-delay: 0.7s;
        }

        .landing-page-container section:nth-of-type(7) {
          animation-delay: 0.8s;
        }

        .landing-page-container section:nth-of-type(8) {
          animation-delay: 0.9s;
        }

        /* Navigation Link Buttons Styling */
        .nav-link-button {
          position: relative;
          overflow: hidden;
        }

        .nav-link-underline {
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #60A5FA, transparent);
          transform: translateX(-50%);
          transition: width 0.3s ease;
        }

        .nav-link-button:hover .nav-link-underline {
          width: 80%;
        }

        .nav-link-button::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.2), transparent);
          transform: translate(-50%, -50%);
          transition: width 0.4s ease, height 0.4s ease;
        }

        .nav-link-button:hover::before {
          width: 120px;
          height: 120px;
        }

        /* Morph Transition Animation for Navigation Buttons */
        @keyframes morphClick {
          0% {
            transform: scale(1) rotate(0deg);
            border-radius: 0.5rem;
            background: transparent;
          }
          25% {
            transform: scale(0.95) rotate(-2deg);
            border-radius: 1rem;
            background: rgba(59, 130, 246, 0.1);
          }
          50% {
            transform: scale(1.05) rotate(2deg);
            border-radius: 0.25rem;
            background: rgba(59, 130, 246, 0.2);
          }
          75% {
            transform: scale(0.98) rotate(-1deg);
            border-radius: 0.75rem;
            background: rgba(59, 130, 246, 0.15);
          }
          100% {
            transform: scale(1) rotate(0deg);
            border-radius: 0.5rem;
            background: transparent;
          }
        }

        @keyframes morphRipple {
          0% {
            transform: scale(0) translate(-50%, -50%);
            opacity: 0.8;
            border-radius: 50%;
          }
          50% {
            transform: scale(1.5) translate(-50%, -50%);
            opacity: 0.4;
            border-radius: 30%;
          }
          100% {
            transform: scale(2.5) translate(-50%, -50%);
            opacity: 0;
            border-radius: 10%;
          }
        }

        @keyframes morphGlow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
          50% {
            box-shadow: 0 0 20px 5px rgba(59, 130, 246, 0.4);
          }
        }

        .nav-link-button.morph-active {
          animation: morphClick 0.6s cubic-bezier(0.4, 0, 0.2, 1), morphGlow 0.6s ease-in-out;
        }

        .nav-link-button.morph-active::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.3), transparent);
          transform: translate(-50%, -50%);
          animation: morphRipple 0.6s ease-out;
          pointer-events: none;
          z-index: 0;
        }

        /* Sign In Button Styling */
        .nav-signin-button {
          position: relative;
          overflow: hidden;
        }

        .nav-signin-glow {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent);
          transition: left 0.5s ease;
        }

        .nav-signin-button:hover .nav-signin-glow {
          left: 100%;
        }

        .nav-signin-button::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.1));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .nav-signin-button:hover::after {
          opacity: 1;
        }

        /* Morph Animation for Sign In Button */
        @keyframes morphSignIn {
          0% {
            transform: scale(1);
            border-radius: 0.5rem;
            border-width: 2px;
          }
          25% {
            transform: scale(0.92);
            border-radius: 0.75rem;
            border-width: 3px;
          }
          50% {
            transform: scale(1.08);
            border-radius: 0.25rem;
            border-width: 1px;
          }
          75% {
            transform: scale(0.96);
            border-radius: 0.6rem;
            border-width: 2.5px;
          }
          100% {
            transform: scale(1);
            border-radius: 0.5rem;
            border-width: 2px;
          }
        }

        @keyframes morphSignInGlow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
          25% {
            box-shadow: 0 0 15px 3px rgba(59, 130, 246, 0.3);
          }
          50% {
            box-shadow: 0 0 25px 5px rgba(59, 130, 246, 0.5);
          }
          75% {
            box-shadow: 0 0 15px 3px rgba(59, 130, 246, 0.3);
          }
        }

        .nav-signin-button.morph-active {
          animation: morphSignIn 0.6s cubic-bezier(0.4, 0, 0.2, 1), morphSignInGlow 0.6s ease-in-out;
        }

        .nav-signin-button.morph-active::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 120px;
          height: 120px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.4), transparent);
          transform: translate(-50%, -50%);
          animation: morphRipple 0.6s ease-out;
          pointer-events: none;
          z-index: 0;
        }

        /* Dark Mode Navigation Buttons */
        [data-theme="dark"] .nav-link-button:hover {
          background: rgba(59, 130, 246, 0.15) !important;
        }

        [data-theme="dark"] .nav-signin-button {
          border-color: rgba(59, 130, 246, 0.4) !important;
        }

        [data-theme="dark"] .nav-signin-button:hover {
          border-color: rgba(96, 165, 250, 0.6) !important;
          background: rgba(59, 130, 246, 0.25) !important;
        }

        /* Mobile Navigation Link Styling */
        .mobile-nav-link {
          position: relative;
        }

        .mobile-nav-underline {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #60A5FA, #3B82F6);
          transition: width 0.3s ease;
        }

        .mobile-nav-link:hover .mobile-nav-underline {
          width: 100%;
        }

        .mobile-nav-link::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
          transition: left 0.4s ease;
        }

        .mobile-nav-link:hover::before {
          left: 100%;
        }

        /* Mobile Sign In Button */
        .mobile-nav-signin {
          position: relative;
          overflow: hidden;
        }

        .mobile-nav-signin-glow {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .mobile-nav-signin:hover .mobile-nav-signin-glow {
          left: 100%;
        }

        /* Mobile Responsive Styles */
        @media (max-width: 640px) {
          /* Dark Mode Toggle - Mobile */
          .dark-mode-toggle-btn {
            top: 0.75rem !important;
            right: 0.75rem !important;
            padding: 0.625rem !important;
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            background: rgba(255, 255, 255, 0.95) !important;
            border: 2px solid rgba(0, 0, 0, 0.1) !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
            z-index: 9999 !important;
          }

          [data-theme="dark"] .dark-mode-toggle-btn {
            background: rgba(31, 41, 55, 0.95) !important;
            border: 2px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
          }

          .dark-mode-toggle-btn svg {
            width: 20px !important;
            height: 20px !important;
          }

          /* Slideshow - Mobile */
          .landing-slideshow {
            height: 280px !important;
            min-height: 280px !important;
            max-height: 320px !important;
            width: 100% !important;
          }

          .landing-slideshow img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            object-position: center !important;
            display: block !important;
          }

          /* Slideshow container wrapper - Mobile */
          .relative.max-w-lg.mx-auto.lg\\:max-w-none {
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
          }

          /* Hero Section - Mobile */
          .hero-section h1 {
            font-size: 2rem !important;
            line-height: 1.2 !important;
            margin-bottom: 1rem !important;
          }

          .hero-section p {
            font-size: 0.875rem !important;
            margin-bottom: 1rem !important;
          }

          /* CTA Buttons - Mobile */
          .hero-cta-button {
            width: 100%;
            justify-content: center;
            padding: 0.875rem 1.5rem !important;
            font-size: 0.875rem !important;
          }

          /* Appointment Button Animation - Mobile Optimization */
          .appointment-button-highlight {
            animation: appointmentPulse 2s ease-in-out infinite !important;
          }

          .appointment-spotlight {
            width: 150px !important;
            height: 150px !important;
            filter: blur(15px) !important;
          }

          .appointment-particles::before,
          .appointment-particles::after {
            width: 4px !important;
            height: 4px !important;
          }

          /* Service Cards - Mobile */
          .service-card {
            padding: 1rem !important;
          }

          .service-icon {
            font-size: 2rem !important;
            margin-bottom: 0.75rem !important;
          }

          /* Benefit Cards - Mobile */
          .benefit-card {
            padding: 1.25rem !important;
          }

          .benefit-icon {
            font-size: 2.5rem !important;
            margin-bottom: 0.75rem !important;
          }

          /* How It Works - Mobile */
          .how-it-works-card {
            padding: 1.25rem !important;
          }

          .step-number {
            font-size: 3rem !important;
            top: 0.5rem !important;
            right: 0.5rem !important;
          }

          /* Stats Section - Mobile */
          .stat-card-animated {
            padding: 1rem !important;
          }

          .stat-icon {
            font-size: 2.5rem !important;
            margin-bottom: 0.5rem !important;
          }

          /* Navigation - Mobile */
          nav {
            padding: 0.75rem 1rem !important;
          }

          /* Cursor Reactive Background - Reduce on Mobile */
          .cursor-reactive-bg {
            opacity: 0.6;
          }

          .cursor-reactive-particle {
            width: 6px !important;
            height: 6px !important;
          }

          .cursor-reactive-glow {
            width: 200px !important;
            height: 200px !important;
            filter: blur(30px) !important;
          }

          /* Section Padding - Mobile */
          section {
            padding-top: 3rem !important;
            padding-bottom: 3rem !important;
          }

          /* Grid Gaps - Mobile */
          .grid {
            gap: 1rem !important;
          }

          /* Text Sizes - Mobile */
          h2 {
            font-size: 1.75rem !important;
          }

          h3 {
            font-size: 1.25rem !important;
          }

          /* Pricing Cards - Mobile */
          .pricing-card {
            padding: 1.5rem !important;
          }
        }

        @media (max-width: 768px) {
          /* Tablet Adjustments */
          .hero-section h1 {
            font-size: 2.5rem !important;
          }

          .service-card {
            padding: 1.25rem !important;
          }

          /* Navigation buttons stack on tablet */
          .nav-buttons {
            flex-direction: column;
            gap: 0.5rem;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) and (pointer: coarse) {
          /* Reduce animations on touch devices for better performance */
          .appointment-button-highlight {
            animation: appointmentPulse 2s ease-in-out infinite !important;
          }

          .cursor-reactive-bg {
            opacity: 0.5;
          }

          /* Larger touch targets */
          button, a {
            min-height: 44px;
            min-width: 44px;
          }
        }

          /* Prevent horizontal scroll on mobile */
          @media (max-width: 640px) {
            * {
              max-width: 100vw;
            }

            body {
              overflow-x: hidden;
            }

            /* Ensure all sections have proper mobile padding */
            section {
              padding-left: 1rem;
              padding-right: 1rem;
            }

            /* Mobile navigation improvements */
            .mobile-menu {
              max-height: calc(100vh - 4rem);
              overflow-y: auto;
            }

            /* Mobile button improvements */
            button {
              touch-action: manipulation;
            }

            /* Mobile text improvements */
            p, span {
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
          }

          /* Tablet specific adjustments */
          @media (min-width: 641px) and (max-width: 1024px) {
            .hero-section h1 {
              font-size: 2.75rem !important;
            }

            .service-card {
              padding: 1.5rem !important;
            }
        }
      `}</style>

      {/* Top Contact Bar - Desktop Only */}
      <div className="top-contact-bar bg-white border-b border-gray-100 py-3 hidden xl:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium truncate">Contact Us</p>
                <a 
                  href="https://wa.me/8801703866084" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors duration-200"
                  style={{ textDecoration: 'none', display: 'block' }}
                >
                  +8801703866084
                </a>
              </div>
            </div>

            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium truncate">Email Support</p>
                <p className="text-sm font-semibold text-gray-900 truncate">wehealbd2025@gmail.com</p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium whitespace-nowrap">24/7 Available & Secure</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-gray-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <div className="flex-shrink-0 cursor-pointer" onClick={handleHomeClick}>
              <img 
                src="https://res.cloudinary.com/dmmetrvda/image/upload/v1766217550/weheal/logo.png" 
                alt="WeHeal" 
                className="h-12 sm:h-16 w-auto"
                onError={(e) => {
                  // Fallback to text logo if image fails
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent && !parent.querySelector('.fallback-logo')) {
                    const fallback = document.createElement('h1');
                    fallback.className = 'fallback-logo text-xl sm:text-2xl font-bold text-white';
                    fallback.innerHTML = 'We<span class="text-blue-400">Heal</span>';
                    parent.appendChild(fallback);
                  }
                }}
              />
            </div>

            {/* Desktop Navigation - All Items in One Line */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6 flex-1 justify-end">
              <button 
                onClick={(e) => handleMorphClick(e, handleHomeClick)} 
                className="nav-link-button text-white hover:text-blue-400 font-medium transition-all duration-300 text-base py-2.5 px-4 rounded-lg hover:bg-white/10 hover:backdrop-blur-sm relative group"
              >
                <span className="relative z-10">Home</span>
                <span className="nav-link-underline"></span>
              </button>
              <a 
                href="#services" 
                onClick={(e) => {
                  e.preventDefault();
                  handleMorphClick(e, () => {
                    document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
                  });
                }}
                className="nav-link-button text-white hover:text-blue-400 font-medium transition-all duration-300 text-base py-2.5 px-4 rounded-lg hover:bg-white/10 hover:backdrop-blur-sm relative group"
              >
                <span className="relative z-10">Services</span>
                <span className="nav-link-underline"></span>
              </a>
              <a 
                href="#how-it-works" 
                onClick={(e) => {
                  e.preventDefault();
                  handleMorphClick(e, () => {
                    document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                  });
                }}
                className="nav-link-button text-white hover:text-blue-400 font-medium transition-all duration-300 text-base py-2.5 px-4 rounded-lg hover:bg-white/10 hover:backdrop-blur-sm relative group"
              >
                <span className="relative z-10">How It Works</span>
                <span className="nav-link-underline"></span>
              </a>
              <a 
                href="#pricing" 
                onClick={(e) => {
                  e.preventDefault();
                  handleMorphClick(e, () => {
                    document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                  });
                }}
                className="nav-link-button text-white hover:text-blue-400 font-medium transition-all duration-300 text-base py-2.5 px-4 rounded-lg hover:bg-white/10 hover:backdrop-blur-sm relative group"
              >
                <span className="relative z-10">Pricing</span>
                <span className="nav-link-underline"></span>
              </a>
              
              {/* Sign In & Appointment Buttons or User Profile */}
              {isAuthenticated ? (
                <>
              <button 
                    onClick={() => handleNavAuthRedirect()}
                    className="flex items-center gap-3 px-4 xl:px-5 py-2 xl:py-2.5 text-white font-medium transition-all duration-300 text-sm xl:text-base whitespace-nowrap rounded-lg border-2 border-white/30 hover:border-blue-400 hover:bg-blue-500/20 hover:backdrop-blur-sm relative overflow-hidden group"
                  >
                    <img 
                      src={currentUser?.profilePicture ? getImageUrl(currentUser.profilePicture) : `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'User')}&background=1E40AF&color=fff&size=128&bold=true`} 
                      alt={currentUser?.name || 'User'} 
                      className="w-8 h-8 xl:w-9 xl:h-9 rounded-full object-cover ring-2 ring-blue-400"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'User')}&background=1E40AF&color=fff&size=128&bold=true`;
                      }}
                    />
                    <span className="relative z-10">{currentUser?.name || 'My Account'}</span>
              </button>
              <button 
                    onClick={() => handleAppointment()}
                    className="appointment-button-highlight px-4 xl:px-6 py-2 xl:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-semibold shadow-lg text-sm xl:text-base whitespace-nowrap relative overflow-hidden"
              >
                    <div className="appointment-particles"></div>
                    <div className="appointment-spotlight"></div>
                    <div className="appointment-shine"></div>
                    <span className="relative z-10">Appointment</span>
              </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={(e) => handleMorphClick(e, handleSignIn)}
                    className="nav-signin-button px-5 xl:px-6 py-2.5 xl:py-3 text-white font-semibold transition-all duration-300 text-sm xl:text-base whitespace-nowrap rounded-lg border-2 border-white/30 hover:border-blue-400 hover:bg-blue-500/20 hover:backdrop-blur-sm relative overflow-hidden group"
                  >
                    <span className="relative z-10">Sign In</span>
                    <span className="nav-signin-glow"></span>
                  </button>
                  <button 
                    onClick={(e) => handleMorphClick(e, handleAppointment)}
                    className="appointment-button-highlight px-4 xl:px-6 py-2 xl:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-semibold shadow-lg text-sm xl:text-base whitespace-nowrap relative overflow-hidden"
                  >
                    <div className="appointment-particles"></div>
                    <div className="appointment-spotlight"></div>
                    <div className="appointment-shine"></div>
                    <span className="relative z-10">Appointment</span>
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsNavOpen(!isNavOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-800"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isNavOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isNavOpen && (
          <div className="lg:hidden bg-gray-800 border-t border-gray-700">
            <div className="px-4 py-4 space-y-2">
              <button 
                onClick={(e) => {
                  handleMorphClick(e, () => {
                    handleHomeClick();
                    setIsNavOpen(false);
                  });
                }} 
                className="mobile-nav-link nav-link-button block w-full text-left py-2.5 px-3 text-white hover:text-blue-400 hover:bg-gray-700/50 rounded-lg font-medium transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10">Home</span>
                <span className="mobile-nav-underline"></span>
              </button>
              <a 
                href="#services" 
                onClick={(e) => {
                  e.preventDefault();
                  handleMorphClick(e, () => {
                    document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
                    setIsNavOpen(false);
                  });
                }} 
                className="mobile-nav-link nav-link-button block py-2.5 px-3 text-white hover:text-blue-400 hover:bg-gray-700/50 rounded-lg font-medium transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10">Services</span>
                <span className="mobile-nav-underline"></span>
              </a>
              <a 
                href="#how-it-works" 
                onClick={(e) => {
                  e.preventDefault();
                  handleMorphClick(e, () => {
                    document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                    setIsNavOpen(false);
                  });
                }} 
                className="mobile-nav-link nav-link-button block py-2.5 px-3 text-white hover:text-blue-400 hover:bg-gray-700/50 rounded-lg font-medium transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10">How It Works</span>
                <span className="mobile-nav-underline"></span>
              </a>
              <a 
                href="#pricing" 
                onClick={(e) => {
                  e.preventDefault();
                  handleMorphClick(e, () => {
                    document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                    setIsNavOpen(false);
                  });
                }} 
                className="mobile-nav-link nav-link-button block py-2.5 px-3 text-white hover:text-blue-400 hover:bg-gray-700/50 rounded-lg font-medium transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10">Pricing</span>
                <span className="mobile-nav-underline"></span>
              </a>
              
              <div className="pt-3 border-t border-gray-700 space-y-2">
                <button 
                  onClick={(e) => { handleMorphClick(e, () => { handleEmergency(); setIsNavOpen(false); }); }}
                  className="emergency-button-highlight w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl relative overflow-hidden"
                >
                  <div className="emergency-particles"></div>
                  <div className="emergency-spotlight"></div>
                  <div className="emergency-shine"></div>
                  <svg className="w-5 h-5 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="relative z-10">Emergency Care</span>
                </button>
                {isAuthenticated ? (
                  <button 
                    onClick={() => { handleNavAuthRedirect(); setIsNavOpen(false); }} 
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-all duration-200 border border-gray-600 hover:border-blue-500"
                  >
                    <img 
                      src={currentUser?.profilePicture ? getImageUrl(currentUser.profilePicture) : `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'User')}&background=1E40AF&color=fff&size=128&bold=true`} 
                      alt={currentUser?.name || 'User'} 
                      className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'User')}&background=1E40AF&color=fff&size=128&bold=true`;
                      }}
                    />
                    <span className="font-medium text-white">{currentUser?.name || 'My Account'}</span>
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={(e) => {
                        handleMorphClick(e, () => {
                          handleSignIn();
                          setIsNavOpen(false);
                        });
                      }}
                      className="mobile-nav-signin nav-signin-button w-full px-4 py-3 text-white bg-gray-700/80 rounded-lg hover:bg-gray-600 transition-all duration-300 font-medium border border-gray-600 hover:border-blue-400 relative overflow-hidden group"
                    >
                      <span className="relative z-10">Sign In</span>
                      <span className="mobile-nav-signin-glow"></span>
                    </button>
                    <button 
                      onClick={(e) => {
                        handleMorphClick(e, () => {
                          handleAppointment();
                          setIsNavOpen(false);
                        });
                      }}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                    >
                      Book Appointment
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white py-12 sm:py-16 lg:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 animate-on-scroll">
              <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Trusted by 50,000+ patients nationwide</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6">
                Medical & Health Care <span className="text-blue-600">Services</span>
              </h1>
              
              <p className="text-base sm:text-lg text-gray-600 mb-3 sm:mb-4 leading-relaxed">
                Your health is our top priority. Connect with licensed doctors online in minutes. Get prescriptions, consultations, and care from the comfort of your home.
              </p>

              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8" style={{ fontFamily: 'Noto Sans Bengali, sans-serif' }}>
                আপনার প্রয়োজনের সময়ে, যে কোনো জায়গা থেকে বিশ্বস্ত স্বাস্থ্যসেবা। অনলাইনে সহজেই পরামর্শ নিন।
              </p>

              {/* Primary CTAs - Advanced Animation */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6">
                <button 
                  onClick={(e) => handleMorphClick(e, handleAppointment)}
                  className="appointment-button-highlight hero-cta-button w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold shadow-lg text-sm sm:text-base relative overflow-hidden"
                >
                  {/* Animated particles flowing towards button */}
                  <div className="appointment-particles"></div>
                  
                  {/* Spotlight effect */}
                  <div className="appointment-spotlight"></div>
                  
                  {/* Shine effect */}
                  <div className="appointment-shine"></div>
                  
                  <span className="inline-flex items-center gap-2 relative z-10">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Appointment
                  </span>
                </button>
                <button 
                  onClick={(e) => handleMorphClick(e, handleEmergency)}
                  className="emergency-button-highlight hero-cta-button w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold shadow-lg text-sm sm:text-base relative overflow-hidden"
                >
                  {/* Animated particles flowing towards button */}
                  <div className="emergency-particles"></div>
                  
                  {/* Spotlight effect */}
                  <div className="emergency-spotlight"></div>
                  
                  {/* Shine effect */}
                  <div className="emergency-shine"></div>
                  
                  <span className="inline-flex items-center gap-2 relative z-10">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Emergency
                  </span>
                </button>
              </div>

              {/* Emergency Disclaimer */}
              <p className="text-xs text-gray-500 flex items-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>For life-threatening emergencies, call 999 immediately.</span>
              </p>
            </div>

            {/* Right Image/Visual - Slideshow */}
            <div className="order-1 lg:order-2 relative animate-on-scroll-delay">
              <div className="relative max-w-lg mx-auto lg:max-w-none">
                <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
                  <ImageSlideshow />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="animated-wave wave-1"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {[
              { title: "24/7 Availability", desc: "Round-the-clock care", icon: "⏰" },
              { title: "Licensed Doctors", desc: "Verified professionals", icon: "👨‍⚕️" },
              { title: "Secure & Private", desc: "HIPAA compliant", icon: "🔒" },
              { title: "Fast Response", desc: "Average wait under 5 min", icon: "⚡" }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-4 sm:p-5 lg:p-6 bg-gray-50 rounded-xl hover:shadow-md transition benefit-card animate-on-scroll" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3 benefit-icon">{item.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Getting quality healthcare has never been easier. Follow these simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                step: "01",
                title: "Choose Service",
                desc: "Select your health concern or specialty from our wide range of medical services."
              },
              {
                step: "02",
                title: "Book Appointment",
                desc: "Pick a convenient time slot and get matched with a licensed doctor instantly."
              },
              {
                step: "03",
                title: "Get Care",
                desc: "Connect via secure video call, receive diagnosis, prescriptions, and follow-up care."
              }
            ].map((item, idx) => (
              <div key={idx} className="relative bg-white p-4 sm:p-6 lg:p-8 rounded-xl border border-gray-200 hover:shadow-lg transition how-it-works-card animate-on-scroll" style={{ animationDelay: `${idx * 0.15}s` }}>
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-100 step-number">{item.step}</div>
                <div className="step-connector"></div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 relative z-10">{item.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm lg:text-base">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Medical Services
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Access specialized care across multiple medical fields with our network of expert doctors.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {[
              { name: "General Physician", icon: "🩺" },
              { name: "Mental Health", icon: "🧠" },
              { name: "Dermatology", icon: "✨" },
              { name: "Pediatrics", icon: "👶" },
              { name: "Cardiology", icon: "❤️" },
              { name: "Gynecology", icon: "🩹" },
              { name: "Orthopedics", icon: "🦴" },
              { name: "ENT Specialist", icon: "👂" }
            ].map((service, idx) => (
              <div 
                key={idx} 
                className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 sm:p-4 lg:p-6 hover:shadow-xl transition cursor-pointer border border-gray-100 hover:border-blue-300 service-card animate-on-scroll"
                style={{ animationDelay: `${idx * 0.08}s` }}
                onClick={handleSignIn}
              >
                <div className="text-2xl sm:text-3xl lg:text-4xl mb-2 sm:mb-3 service-icon relative z-10">{service.icon}</div>
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg mb-1 sm:mb-2 relative z-10">{service.name}</h3>
                <p className="text-xs text-gray-600 mb-2 sm:mb-3 relative z-10">Expert consultation 24/7</p>
                <button className="text-blue-600 text-xs font-medium hover:text-blue-700 service-button relative z-10">
                  Book Now →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Advanced Animation */}
      <section className="stats-section py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(30, 64, 175, 0.3) 0%, transparent 50%),
                              radial-gradient(circle at 80% 70%, rgba(37, 99, 235, 0.2) 0%, transparent 50%)`,
            animation: 'pulse 8s ease-in-out infinite'
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 text-center">
            {[
              { value: 50000, suffix: "+", label: "Happy Patients", icon: "👥" },
              { value: 200, suffix: "+", label: "Expert Doctors", icon: "👨‍⚕️" },
              { value: 150, suffix: "k+", label: "Consultations", icon: "💬" },
              { value: 98, suffix: "%", label: "Success Rate", icon: "⭐" }
            ].map((stat, idx) => (
              <div key={idx} className="stat-card-animated animate-on-scroll" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3 stat-icon">{stat.icon}</div>
                <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
                  <span className="counter-value" data-target={stat.value}>{stat.value}</span>
                  {stat.suffix}
                </p>
                <p className="text-gray-300 text-xs sm:text-sm lg:text-base font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Your Safety & Privacy Matter
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
                We take your health data seriously. All consultations are encrypted and secure.
              </p>
              <div className="space-y-4">
                {[
                  { title: "HIPAA Compliant", desc: "Protected under healthcare privacy laws" },
                  { title: "Licensed Professionals", desc: "All doctors are verified and certified" },
                  { title: "Encrypted Communications", desc: "End-to-end encryption for all calls" },
                  { title: "Secure Payment", desc: "PCI-compliant payment processing" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">{item.title}</p>
                      <p className="text-xs sm:text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center">
              <svg className="w-24 h-24 sm:w-32 sm:h-32 mx-auto text-gray-900 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">100% Secure Platform</h3>
              <p className="text-gray-600 text-sm sm:text-base">Your trust is our foundation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your healthcare needs. No hidden fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-gray-200 hover:border-gray-400 transition">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Pay Per Visit</h3>
              <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">Perfect for occasional consultations</p>
              <div className="mb-4 sm:mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">৳29</span>
                <span className="text-gray-600">/consultation</span>
              </div>
              <ul className="space-y-3 mb-6 sm:mb-8">
                {[
                  "15-minute video consultation",
                  "Licensed doctor access",
                  "Digital prescription",
                  "Follow-up chat for 24 hours"
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={handleSignIn}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm sm:text-base"
              >
                Get Started
              </button>
            </div>

            <div className="bg-gray-900 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                POPULAR
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Monthly Plan</h3>
              <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Unlimited access for your family</p>
              <div className="mb-4 sm:mb-6">
                <span className="text-4xl sm:text-5xl font-bold">৳79</span>
                <span className="text-gray-400">/month</span>
              </div>
              <ul className="space-y-3 mb-6 sm:mb-8">
                {[
                  "Unlimited consultations",
                  "Priority booking",
                  "Family coverage (up to 4)",
                  "24/7 chat support",
                  "Mental health sessions",
                  "Free prescription delivery"
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base">
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={handleSignIn}
                className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold text-sm sm:text-base"
              >
                Start Free Trial
              </button>
            </div>
          </div>

          <p className="text-center text-gray-600 mt-6 sm:mt-8 text-xs sm:text-sm">
            All plans include secure payment processing and can be canceled anytime.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {[
              {
                q: "How quickly can I see a doctor?",
                a: "Most patients are connected with a doctor within 5 minutes. Our platform is available 24/7."
              },
              {
                q: "Are the doctors licensed?",
                a: "Yes, all doctors are board-certified, licensed professionals who have been thoroughly verified."
              },
              {
                q: "What if I need emergency care?",
                a: "For life-threatening emergencies, call 999 immediately. Our Emergency Care is for urgent but non-life-threatening conditions."
              },
              {
                q: "Can I get prescriptions online?",
                a: "Yes, if medically appropriate, your doctor can send prescriptions directly to your pharmacy."
              },
              {
                q: "Is my information secure?",
                a: "Absolutely. We use bank-level encryption and are HIPAA compliant."
              },
              {
                q: "What if I'm not satisfied?",
                a: "We offer a 100% satisfaction guarantee. Contact us within 24 hours for a full refund."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 hover:shadow-md transition">
                <h3 className="font-semibold text-base sm:text-lg text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
            Ready to Take Control of Your Health?
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 mb-6 sm:mb-8">
            Join thousands of patients who trust WeHeal for their healthcare needs.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <button 
              onClick={(e) => handleMorphClick(e, handleAppointment)}
              className="appointment-button-highlight w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold shadow-lg text-sm sm:text-base relative overflow-hidden"
            >
              <div className="appointment-particles"></div>
              <div className="appointment-spotlight"></div>
              <div className="appointment-shine"></div>
              <span className="relative z-10">Book Your First Appointment</span>
            </button>
            <button 
              onClick={handleSignUp}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-transparent text-white rounded-lg hover:bg-white/10 transition font-semibold border-2 border-white text-sm sm:text-base"
            >
              Create Free Account
            </button>
          </div>
          <p className="text-xs sm:text-sm text-gray-400">
            ✓ No credit card required  •  ✓ Cancel anytime  •  ✓ 24/7 support
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 sm:py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <h3 className="text-white font-bold text-lg sm:text-xl mb-3 sm:mb-4">WeHeal</h3>
              <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                Trusted healthcare whenever you need it.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Quick Links</h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li><button onClick={handleHomeClick} className="hover:text-white transition">Home</button></li>
                <li><a href="#services" className="hover:text-white transition">Services</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition">How It Works</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Services</h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li><button onClick={handleSignIn} className="hover:text-white transition">Online Consultation</button></li>
                <li><button onClick={handleSignIn} className="hover:text-white transition">Emergency Care</button></li>
                <li><button onClick={handleSignIn} className="hover:text-white transition">Lab Tests</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Contact</h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>
                  <a href="mailto:wehealbd2025@gmail.com" className="hover:text-white transition">wehealbd2025@gmail.com</a>
                </li>
                <li>823-4565-13456</li>
                <li>Merul Badda, Dhaka</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-6 sm:pt-8 mt-6 sm:mt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs sm:text-sm">
              <p>© {new Date().getFullYear()} WeHeal. All rights reserved.</p>
              <div className="flex gap-4 sm:gap-6">
                <a href="#" className="hover:text-white transition">Privacy</a>
                <a href="#" className="hover:text-white transition">Terms</a>
                <a href="#" className="hover:text-white transition">Cookies</a>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-4 text-center sm:text-left">
              ⚠️ For life-threatening emergencies, call 999 immediately.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
